import React, { useEffect, useState } from 'react';
import { Card, CardBody, Input } from '@heroui/react';
import { TFinanceProjectAssumptions, TFinancialMetrics } from '../dcfTypes';

type VesselPropertiesProps = {
	financialMetrics: TFinancialMetrics;
	financeProjectAssumptions: TFinanceProjectAssumptions;
	ltvAtFinance: number | null;
	onChange: (field: keyof TFinanceProjectAssumptions, value: number) => void;
	onLtvChange: (value: number) => void;
};

type AssumptionRowProps = {
	label: string;
	value: number | string | null;
	unit: string;
	extra?: string;
	editable?: boolean;
	readonly?: boolean;
	min?: number;
	max?: number;
	onValueChange?: (value: number) => void;
};

const AssumptionRow: React.FC<AssumptionRowProps> = ({
	label,
	value,
	unit,
	extra,
	editable,
	readonly,
	min,
	max,
	onValueChange,
}) => {
	const [localStr, setLocalStr] = useState(() => (typeof value === 'number' ? String(value) : ''));

	// Sync when the parent resets the value (e.g. Reset button)
	useEffect(() => {
		if (typeof value === 'number' && parseFloat(localStr) !== value) {
			setLocalStr(String(value));
		}
	}, [value]);

	const handleChange = (v: string) => {
		setLocalStr(v);
		const num = parseFloat(v);
		if (!isNaN(num) && onValueChange) onValueChange(num);
	};

	return (
		<div className="grid grid-cols-4 gap-x-3 border-b border-dotted pb-0.5 items-center whitespace-nowrap">
			<span className="font-semibold text-msi-orange-color-400">{label}</span>
			{editable && onValueChange ? (
				<Input
					type="number"
					size="sm"
					variant="bordered"
					value={localStr}
					step="any"
					min={min}
					max={max}
					onValueChange={handleChange}
					classNames={{
						base: 'w-24 ml-auto',
						inputWrapper: 'h-6 min-h-6 border-gray-300 rounded pe-0',
						input: 'text-left text-xs [&::-webkit-inner-spin-button]:!appearance-auto [&::-webkit-inner-spin-button]:!opacity-0 [&:hover::-webkit-inner-spin-button]:!opacity-100 [&:focus::-webkit-inner-spin-button]:!opacity-100 [&::-webkit-outer-spin-button]:!appearance-auto [&::-webkit-outer-spin-button]:!opacity-0 [&:hover::-webkit-outer-spin-button]:!opacity-100 [&:focus::-webkit-outer-spin-button]:!opacity-100',
					}}
				/>
			) : readonly ? (
				<Input
					type="number"
					size="sm"
					variant="bordered"
					isReadOnly
					value={typeof value === 'number' ? String(value) : ''}
					classNames={{
						base: 'w-24 ml-auto',
						inputWrapper: 'h-6 min-h-6 border-gray-300 rounded bg-gray-50',
						input: 'text-left text-xs text-gray-400 cursor-default',
					}}
				/>
			) : (
				<span className="text-left text-sm">{typeof value === 'number' ? value : (value ?? '-')}</span>
			)}
			<span className="text-right text-sm">{unit}</span>
			<span className="text-right text-xs text-gray-500">{extra || ''}</span>
		</div>
	);
};

const VesselAssumptions: React.FC<VesselPropertiesProps> = ({
	financeProjectAssumptions,
	ltvAtFinance,
	onChange,
	onLtvChange,
}) => {

	return (
		<Card className="bg-white w-full h-full shadow-none border border-msi-darkgrey-color-300 rounded">
			<CardBody className="h-full p-4">
				<div className="border-t-2 border-b-2 border-msi-orange-color py-2 h-full">
					<div className="grid gap-1 text-sm text-gray-700">
						<AssumptionRow
							label="Investment Period"
							value={financeProjectAssumptions.period}
							unit="Years"
							readonly
						/>
						<AssumptionRow
							label="WACC"
							value={financeProjectAssumptions.wacc}
							unit="%"
							readonly
						/>
						<AssumptionRow
							label="Cost Of Equity"
							value={financeProjectAssumptions.costOfEquity}
							unit="%"
							editable
							min={0}
							max={50}
							onValueChange={(v) => onChange('costOfEquity', v)}
						/>
						<AssumptionRow
							label="Purchase Value"
							value={financeProjectAssumptions.purchasePrice}
							unit="$ Mn"
							editable
							min={0.1}
							onValueChange={(v) => onChange('purchasePrice', v)}
						/>
						<AssumptionRow
							label="Sale Value"
							value={financeProjectAssumptions.salePrice}
							unit="$ Mn"
							extra={financeProjectAssumptions.salePriceAge != null ? `(@${financeProjectAssumptions.salePriceAge}Yrs)` : ''}
							editable
							min={0.1}
							onValueChange={(v) => onChange('salePrice', v)}
						/>
						<AssumptionRow
							label="Average Earnings (b)"
							value={financeProjectAssumptions.averageEarnings}
							unit="$ k/Day"
							editable
							min={0}
							onValueChange={(v) => onChange('averageEarnings', v)}
						/>
						<AssumptionRow
							label="Average Opex"
							value={financeProjectAssumptions.averageOpex}
							unit="$ k/Day"
							editable
							min={0}
							onValueChange={(v) => onChange('averageOpex', v)}
						/>
						<AssumptionRow
							label="LTV"
							value={ltvAtFinance}
							unit="%"
							editable
							min={0}
							max={100}
							onValueChange={onLtvChange}
						/>
						<AssumptionRow
							label="Balloon"
							value={financeProjectAssumptions.balloon}
							unit="$ Mn"
							editable
							min={0}
							onValueChange={(v) => onChange('balloon', v)}
						/>
						<AssumptionRow
							label="Interest Rate Margin"
							value={financeProjectAssumptions.interestRateMargin}
							unit="%"
							extra="(+ Base Rate)"
							editable
							min={0}
							max={20}
							onValueChange={(v) => onChange('interestRateMargin', v)}
						/>
						<AssumptionRow
							label="On Hire"
							value={financeProjectAssumptions.onHire}
							unit="Days"
							editable
							min={0}
							max={365}
							onValueChange={(v) => onChange('onHire', v)}
						/>
						<AssumptionRow
							label="Operating Days"
							value={financeProjectAssumptions.operatingDays}
							unit="Days"
							editable
							min={1}
							max={365}
							onValueChange={(v) => onChange('operatingDays', v)}
						/>
					</div>
				</div>
			</CardBody>
		</Card>
	);
};

export default VesselAssumptions;
